import React, { useState } from "react"
import { Button, Checkbox, Input } from "antd"
import {
  PRIMARY_BTN_STYLE,
  flattenGonSelStandards,
  swapConnectionWord,
} from "./constants"
import ConnectionTypeToggle from "./ConnectionTypeToggle"

// Badged purely on provenance — is this code's data literally in the document the user just
// uploaded, or Rei's own call. There's nothing to badge for data that isn't shown at all (see
// cardCodes below), so this only ever needs to describe THIS run's own extraction.
const statusBadgeFor = (source) => (source === "document"
  ? { text: "From your document", color: "#5a6a7a", background: "#eef1f3" }
  : { text: "Rei recommended", color: "#2f9e8f", background: "#e4f2ef" })

// Set-valued keep/accept for sel_standards, styled as the same description cards as
// ChecklistQuestion's brand-new-activity flow. Cards shown, and selected by default, are exactly
// aiCodes — this run's own extraction, nothing more (no leftover indicator from a PAST upload
// that this document and Rei both have nothing fresh to say about) and nothing less (every code
// this run's extraction produced is included unless the user taps it off). A code already saved
// on the matrix that this run doesn't reconfirm is simply dropped on save, same as any field the
// document doesn't cover — see GradePicker.jsx for the same "don't blend in stale saved data"
// precedent. currentStandards is only consulted for the "Existing:"/"Rei suggests:" comparison
// below, for a code that's in both, never to add a code of its own to what's shown. Codes outside
// what aiStandards produced stay a plain checklist behind "+ Add another indicator", same escape
// hatch as ChecklistQuestion.
const KeepOrAcceptChecklist = ({ wizardMeta, answered, onRespond }) => {
  const {
    currentStandards, aiStandards, documentConnectionExplanation, documentSelIndicator,
  } = wizardMeta
  const aiCodes = Object.keys(aiStandards || {})
  const catalog = flattenGonSelStandards()
  const cardCodes = [...aiCodes].sort()
  const otherCodes = Object.keys(catalog)
    .filter((code) => !cardCodes.includes(code))
    .sort()

  const [selectedCodes, setSelectedCodes] = useState(cardCodes)
  const [showMore, setShowMore] = useState(false)
  const infoFor = (code) => (aiStandards && aiStandards[code]) || {}
  // The document's own verbatim text wins over Rei's connection_explanation for a
  // "document"-sourced code — Rei's value there can be a paraphrase rather than the raw column
  // text (see the extraction prompt), and defaulting to the paraphrase silently substituted
  // Rei's rewrite for what the document actually said. Falls back to whichever explanation
  // would otherwise apply (AI's own for an "ai"-sourced code, else the existing saved one) when
  // there's no document text to prefer.
  // Scoped to the primary document-named code only (code === documentSelIndicator) —
  // documentConnectionExplanation is the SHARED row-level text tied to that one specific code,
  // not a generic fallback for any "document"-sourced code. A different code the document names
  // alongside it (e.g. "1D, 1A, 4C") uses its own connection_explanation directly instead,
  // whether that's blank (unreviewed) or Rei's own independently-reviewed text.
  const explanationFor = (code) => {
    const info = infoFor(code)
    const isPrimary = code === documentSelIndicator
    if (isPrimary && info.connection_explanation && documentConnectionExplanation) {
      return documentConnectionExplanation
    }
    return info.connection_explanation || ""
  }
  const [connectionTypes, setConnectionTypes] = useState(() => cardCodes.reduce((acc, code) => {
    acc[code] = infoFor(code).connection_type || "promotes"
    return acc
  }, {}))
  // Seeded via explanationFor — editing one just overrides that starting value, same as
  // connectionTypes does for connection_type.
  const [explanations, setExplanations] = useState(() => cardCodes.reduce((acc, code) => {
    acc[code] = explanationFor(code)
    return acc
  }, {}))

  const toggleCard = (code) => {
    if (answered) return
    setSelectedCodes((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]))
  }

  const setConnectionType = (code, type, e) => {
    e.stopPropagation()
    if (answered) return
    setConnectionTypes((prev) => ({ ...prev, [code]: type }))
    setExplanations((prev) => ({
      ...prev,
      [code]: swapConnectionWord(prev[code], type),
    }))
  }

  const setExplanation = (code, value) => {
    setExplanations((prev) => ({ ...prev, [code]: value }))
  }

  const handleConfirm = () => {
    const selectedStandards = selectedCodes.reduce((acc, code) => {
      const base = (aiStandards && aiStandards[code])
        || (currentStandards && currentStandards[code]) || {
        connection_type: "promotes",
        connection_explanation: "",
      }
      const hasEdit = explanations[code] !== undefined
      acc[code] = {
        ...base,
        connection_type: connectionTypes[code] || base.connection_type,
        connection_explanation: hasEdit
          ? explanations[code]
          : base.connection_explanation,
      }
      return acc
    }, {})
    onRespond({ selectedCodes, sel_standards: selectedStandards })
  }

  return (
    <div className="wizard-keep-or-accept-checklist" style={{ marginTop: 8 }}>
      {cardCodes.map((code) => {
        const currentInfo = (currentStandards && currentStandards[code]) || null
        // Every code here is drawn from aiCodes (see cardCodes above), so aiStandards[code]
        // always exists — no need to fall back to currentInfo the way infoFor does elsewhere.
        const info = aiStandards[code]
        const selected = selectedCodes.includes(code)
        const statusBadge = statusBadgeFor(info.source)

        const currentExplanation = currentInfo && currentInfo.connection_explanation
        const aiExplanation = info.connection_explanation
        // Only worth showing both when they actually disagree — otherwise it's the same
        // sentence twice, which is exactly the "where's the AI content" confusion this replaces.
        // Also only when THIS code is genuinely document-sourced: comparing the school's saved
        // value against a pure Rei guess (source: "ai", no document backing at all) implies a
        // false equivalence — Rei's own suggestion isn't authoritative document data, so it's
        // shown plainly instead, same as an "ai"-sourced card would be with no existing match.
        const explanationsDiffer = info.source === "document"
          && !!currentExplanation
          && !!aiExplanation
          && currentExplanation !== aiExplanation

        const isPrimaryDocumentCode = info.source === "document" && code === documentSelIndicator
        // The editable box below defaults to the document's own verbatim text (see
        // explanationFor), so this is only worth surfacing when Rei's own connection_explanation
        // for this code actually diverges from it — otherwise it's the same sentence twice.
        // Scoped to the primary code only — see explanationFor above for why
        // documentConnectionExplanation doesn't apply to any other code.
        const showAiSuggestion = isPrimaryDocumentCode
          && documentConnectionExplanation
          && info.connection_explanation
          && documentConnectionExplanation !== info.connection_explanation
        // The other side of showAiSuggestion: when Rei's explanation IS the document's own text
        // verbatim, that's Rei having reviewed it and found nothing to improve — worth saying
        // explicitly rather than leaving it silent.
        const reiConfirmedDocument = isPrimaryDocumentCode
          && documentConnectionExplanation
          && documentConnectionExplanation === info.connection_explanation
        // A different document-named code (not the primary) that Rei has independently reviewed
        // and agreed fits — enrich_sel_standards_with_ai_review flips transcribed to false only
        // when it agrees, so this is never true for one it hasn't reviewed or disagreed with.
        const reiAgreesWithDocumentIndicator = info.source === "document"
          && !info.transcribed
          && code !== documentSelIndicator
          && !!info.connection_explanation

        const connectionType = connectionTypes[code] || info.connection_type || "promotes"

        return (
          <div
            key={code}
            role="button"
            tabIndex={0}
            onClick={() => toggleCard(code)}
            onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && toggleCard(code)}
            style={{
              display: "block",
              width: "100%",
              textAlign: "left",
              cursor: answered ? "default" : "pointer",
              background: selected ? "#f4f8f7" : "#fff",
              border: `1px solid ${selected ? "#2f9e8f" : "#e2e2e2"}`,
              opacity: selected ? 1 : 0.5,
              borderRadius: 8,
              padding: "10px 14px",
              marginBottom: 8,
            }}
          >
            <div style={{ marginBottom: 4 }}>
              <strong>
                {`${code}. ${catalog[code] ? catalog[code].category : ""}`}
              </strong>
              {/* The category alone (e.g. "Self-Awareness") is shared by several codes and
                  doesn't say what THIS specific code means — without the actual definition, a
                  wrong suggestion (e.g. a code about personal responsibility attached to text
                  about naming emotions) isn't visibly wrong at all. */}
              {catalog[code] && catalog[code].label && (
                <div style={{ fontSize: 12, color: "#5a6a7a", marginTop: 2 }}>
                  {catalog[code].label}
                </div>
              )}
            </div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 4,
                flexWrap: "wrap",
              }}
            >
              <ConnectionTypeToggle
                code={code}
                value={connectionType}
                disabled={answered}
                onChange={setConnectionType}
              />
              {statusBadge && (
                <span
                  style={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: statusBadge.color,
                    background: statusBadge.background,
                    borderRadius: 4,
                    padding: "2px 7px",
                  }}
                >
                  {statusBadge.text}
                </span>
              )}
            </div>
            {explanationsDiffer && (
              <>
                <p
                  style={{ margin: "0 0 4px", fontSize: 13, color: "#5a6a7a" }}
                >
                  <strong>Existing: </strong>
                  {currentExplanation}
                </p>
                <p
                  style={{ margin: "0 0 8px", fontSize: 13, color: "#5a6a7a" }}
                >
                  <strong>REI suggests </strong>
                  {aiExplanation}
                </p>
              </>
            )}
            {showAiSuggestion && (
              <div style={{ fontSize: 12, color: "#5a6a7a", marginBottom: 6 }}>
                <strong>{"Rei suggests: "}</strong>
                {`"${info.connection_explanation}"`}
              </div>
            )}
            {reiConfirmedDocument && (
              <div style={{ fontSize: 12, color: "#2f9e8f", marginBottom: 6 }}>
                ✓ Rei reviewed this and agrees it&apos;s clear
              </div>
            )}
            {reiAgreesWithDocumentIndicator && (
              <div style={{ fontSize: 12, color: "#2f9e8f", marginBottom: 6 }}>
                {"✓ Rei also reviewed this indicator and agrees it fits: "}
                {`"${info.connection_explanation}"`}
              </div>
            )}
            <Input.TextArea
              value={explanations[code] || ""}
              onChange={(e) => setExplanation(code, e.target.value)}
              onClick={(e) => e.stopPropagation()}
              onKeyDown={(e) => e.stopPropagation()}
              disabled={answered}
              placeholder="Describe how this activity promotes or reinforces this indicator..."
              autoSize={{ minRows: 2, maxRows: 4 }}
              style={{
                fontSize: 13,
                marginTop: explanationsDiffer ? 0 : "12px",
              }}
            />
          </div>
        )
      })}

      {otherCodes.length > 0 && !answered && !showMore && (
        <Button
          type="link"
          style={{ padding: 0, height: "auto" }}
          onClick={() => setShowMore(true)}
        >
          + Add another indicator
        </Button>
      )}

      {otherCodes.length > 0 && showMore && (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            gap: 6,
            marginTop: 6,
            marginBottom: 6,
          }}
        >
          {otherCodes.map((code) => (
            <div key={code}>
              <Checkbox
                checked={selectedCodes.includes(code)}
                onChange={() => toggleCard(code)}
                disabled={answered}
              >
                <strong>{code}</strong>
                {catalog[code] && `. ${catalog[code].label}`}
              </Checkbox>
              {/* Always its own line below the definition, regardless of text length — sharing
                  a flex row with the checkbox let a short definition (e.g. "1D. Students have a
                  sense of personal responsibility.") keep the toggle inline on the same line
                  while a longer one wrapped it below, so the toggle's position shifted per item
                  instead of staying in a fixed, predictable place. */}
              <div style={{ marginTop: 4 }}>
                <ConnectionTypeToggle
                  code={code}
                  value={connectionTypes[code] || "promotes"}
                  disabled={answered}
                  onChange={setConnectionType}
                />
              </div>
              {selectedCodes.includes(code) && (
                <Input.TextArea
                  value={explanations[code] || ""}
                  onChange={(e) => setExplanation(code, e.target.value)}
                  disabled={answered}
                  placeholder="Describe how this activity promotes or reinforces this indicator..."
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  style={{ marginTop: 4 }}
                />
              )}
            </div>
          ))}
        </div>
      )}

      {!answered && (
        <Button
          style={{ ...PRIMARY_BTN_STYLE, marginTop: 10 }}
          onClick={handleConfirm}
          disabled={selectedCodes.length === 0}
        >
          Confirm indicators
        </Button>
      )}
    </div>
  )
}

export default KeepOrAcceptChecklist
